import * as THREE from 'three';
import EventEmitter from 'eventemitter3';
import { HandGestureTracker } from '@/xr/handInput';
import { rayPanelUv, type PointerRay } from '@/xr/panelRay';
import { colors } from '@/utils/colors';

export type InfoPage = {
  title: string;
  /**
   * Paragraphs, in order. A line starting with '• ' is drawn as a bullet with a
   * hanging indent; anything else is a plain wrapped paragraph. A '' emits a
   * blank half-line.
   */
  body: string[];
};

export type InfoPanelOptions = {
  /**
   * Page content, re-evaluated on every redraw so live values (bridge URL,
   * connection state) are current whenever the panel is drawn.
   */
  pages: () => InfoPage[];
  /** Panel width in metres. */
  widthMeters?: number;
  /** Distance in front of the head when opened. */
  distance?: number;
};

interface EventMap {
  openChanged: (open: boolean) => void;
}

const CANVAS_W = 1024;
const CANVAS_H = 800;
const MARGIN = 56;
const NAV_H = 96;
const TITLE_SIZE = 46;
const BODY_SIZE = 30;
const LINE_H = 42;

// Poke thresholds — same feel as the wrist menu, which these are ported from
const HOVER_RANGE = 0.08;
const PRESS_DEPTH = 0.015;
const REPRESS_S = 0.3;
/** How long a ray-triggered button stays lit. Pokes light while held instead. */
const PRESS_FLASH_S = 0.18;

type Region = { id: 'back' | 'next' | 'close'; x: number; y: number; w: number; h: number };

/**
 * Greedy word wrap. There is no wrapping helper anywhere else in this repo —
 * every other canvas panel draws single lines and relies on fillText's
 * squeeze-to-fit maxWidth, which is unusable for paragraphs.
 */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    // `|| !line` lets a single over-long word occupy its own line rather than
    // looping forever trying to fit it
    if (ctx.measureText(candidate).width <= maxWidth || !line) {
      line = candidate;
    } else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * Paged setup/help panel for in-session reading — the DOM instructions on the
 * flat page are invisible inside an immersive session.
 *
 * Parked rather than head-locked: it snaps in front of the head once on open and
 * then stays put, because paragraphs on a target that eases every frame are
 * unreadable. Poked with either index fingertip; unlike the wrist menu there is
 * no wearer hand, so both hands are tested.
 */
export class InfoPanel extends EventEmitter<EventMap> {
  object: THREE.Mesh;
  isOpen = false;
  page = 0;

  #pages: () => InfoPage[];
  #distance: number;
  #width: number;
  #height: number;
  #pageCount = 1;

  #canvas: HTMLCanvasElement;
  #ctx: CanvasRenderingContext2D;
  #texture: THREE.CanvasTexture;
  #cacheKey = '';
  #regions: Region[] = [];

  #hover?: string;
  #pressed?: string;
  #prevDepth = new Map<string, number>();
  #lastPressAt = -Infinity;
  #pressFlashUntil = 0;
  #elapsed = 0;

  // scratch
  #local = new THREE.Vector3();
  #headFwd = new THREE.Vector3();

  constructor(options: InfoPanelOptions) {
    super();
    this.#pages = options.pages;
    this.#distance = options.distance ?? 1.2;
    this.#width = options.widthMeters ?? 0.8;
    this.#height = this.#width * (CANVAS_H / CANVAS_W);

    this.#canvas = document.createElement('canvas');
    this.#canvas.width = CANVAS_W;
    this.#canvas.height = CANVAS_H;
    const ctx = this.#canvas.getContext('2d');
    if (!ctx) throw new Error('Unable to get 2d context for info panel');
    this.#ctx = ctx;
    this.#texture = new THREE.CanvasTexture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;
    this.#texture.anisotropy = 4; // read at an angle when parked off-centre

    this.object = new THREE.Mesh(
      new THREE.PlaneGeometry(this.#width, this.#height),
      new THREE.MeshBasicMaterial({
        map: this.#texture,
        transparent: true,
        depthTest: false, // always readable, never clipped by the world
        fog: false,
        side: THREE.DoubleSide,
      })
    );
    this.object.name = 'InfoPanel';
    // Above the hint (30) so text isn't punched through, below the wrist menu
    // (40) which stays pokeable while this is up
    this.object.renderOrder = 35;
    this.object.userData.mrMaskExempt = true;
    this.object.visible = false;

    this.#buildRegions();
    this.#draw();
  }

  #buildRegions() {
    const y = (CANVAS_H - NAV_H) / CANVAS_H;
    const h = (NAV_H - MARGIN / 2) / CANVAS_H;
    const w = 0.26;
    this.#regions = [
      { id: 'back', x: 0.06, y, w, h },
      { id: 'next', x: 0.37, y, w, h },
      { id: 'close', x: 0.68, y, w, h },
    ];
  }

  /** Snap in front of the head and show. Re-opening resets to the first page. */
  open(head: { position: THREE.Vector3; quaternion: THREE.Quaternion }) {
    this.page = 0;
    this.#headFwd.set(0, 0, -1).applyQuaternion(head.quaternion);
    this.object.position.copy(head.position).addScaledVector(this.#headFwd, this.#distance);
    // Yaw-only facing so text stays upright regardless of head roll/pitch
    this.object.rotation.set(0, Math.atan2(this.#headFwd.x, this.#headFwd.z) + Math.PI, 0);
    this.#setOpen(true);
  }

  close() {
    this.#setOpen(false);
  }

  toggle(head: { position: THREE.Vector3; quaternion: THREE.Quaternion }) {
    if (this.isOpen) this.close();
    else this.open(head);
  }

  #setOpen(open: boolean) {
    if (open === this.isOpen) return;
    this.isOpen = open;
    this.object.visible = open;
    this.#hover = undefined;
    this.#pressed = undefined;
    this.#pressFlashUntil = 0;
    this.#prevDepth.clear();
    if (open) this.#draw();
    this.emit('openChanged', open);
  }

  /** Poke handling. Call every frame while presenting. */
  update(dt: number, hands: { left: HandGestureTracker; right: HandGestureTracker }) {
    this.#elapsed += dt;
    if (!this.isOpen) return;

    let hover: string | undefined;
    let pressed: string | undefined;
    let bestDepth = Infinity;

    // No wearer hand here, so either index fingertip can poke — take whichever
    // is nearest the surface this frame
    for (const handedness of ['left', 'right'] as const) {
      const tracker = hands[handedness];
      if (!tracker.tracked) {
        this.#prevDepth.set(handedness, NaN);
        continue;
      }
      this.#local.copy(tracker.indexTip);
      this.object.worldToLocal(this.#local);
      const depth = this.#local.z;
      const previous = this.#prevDepth.get(handedness) ?? NaN;
      this.#prevDepth.set(handedness, depth);

      if (depth <= -PRESS_DEPTH || depth >= HOVER_RANGE) continue;
      const u = this.#local.x / this.#width + 0.5;
      const v = 0.5 - this.#local.y / this.#height;
      const region = this.#regions.find(r => u >= r.x && u <= r.x + r.w && v >= r.y && v <= r.y + r.h);
      if (!region || Math.abs(depth) >= Math.abs(bestDepth)) continue;

      bestDepth = depth;
      hover = region.id;
      // Rising edge only. NaN (fresh tracking) fails the comparison, so a hand
      // that re-acquires already through the surface can't fire a phantom press.
      if (
        depth <= PRESS_DEPTH
        && previous > PRESS_DEPTH
        && this.#elapsed - this.#lastPressAt > REPRESS_S
      ) {
        pressed = region.id;
        this.#lastPressAt = this.#elapsed;
      }
    }

    if (pressed) this.#activate(pressed);
    if (hover !== this.#hover || pressed !== this.#pressed) {
      this.#hover = hover;
      this.#pressed = pressed;
    }
    // Cheap: the cache key short-circuits unless something actually changed
    this.#draw();
  }

  /**
   * Hover handling for controller rays — the counterpart to `update`, for a
   * player holding controllers and so with no finger free to poke with.
   * Returns the ray that landed so the caller can rest its pointer dot on it.
   */
  updateRay(dt: number, rays: readonly PointerRay[]): { rayIndex: number; distance: number; point: THREE.Vector3 } | undefined {
    this.#elapsed += dt;
    if (!this.isOpen) return undefined;

    let best: { rayIndex: number; distance: number; point: THREE.Vector3 } | undefined;
    let hover: string | undefined;
    for (let i = 0; i < rays.length; i++) {
      const hit = rayPanelUv(rays[i], this.object, this.#width, this.#height);
      if (!hit || (best && hit.distance >= best.distance)) continue;
      best = { rayIndex: i, distance: hit.distance, point: hit.point };
      hover = this.#regions.find(r => hit.u >= r.x && hit.u <= r.x + r.w && hit.v >= r.y && hit.v <= r.y + r.h)?.id;
    }

    const pressed = this.#elapsed < this.#pressFlashUntil ? this.#pressed : undefined;
    if (hover !== this.#hover || pressed !== this.#pressed) {
      this.#hover = hover;
      this.#pressed = pressed;
    }
    this.#draw(); // cache key makes this a no-op unless something changed
    return best;
  }

  /** Click whatever the ray is hovering (wired to the controller trigger). */
  activateHover(): boolean {
    if (!this.isOpen || !this.#hover) return false;
    if (this.#elapsed - this.#lastPressAt <= REPRESS_S) return false;
    this.#lastPressAt = this.#elapsed;
    this.#pressed = this.#hover;
    this.#pressFlashUntil = this.#elapsed + PRESS_FLASH_S;
    const id = this.#hover;
    this.#draw();
    this.#activate(id);
    return true;
  }

  #activate(id: string) {
    if (id === 'close') {
      this.close();
    } else if (id === 'next') {
      this.page = Math.min(this.page + 1, this.#pageCount - 1);
    } else if (id === 'back') {
      this.page = Math.max(this.page - 1, 0);
    }
  }

  #draw() {
    const pages = this.#pages();
    this.#pageCount = Math.max(1, pages.length);
    this.page = Math.min(this.page, this.#pageCount - 1);
    const current = pages[this.page];
    if (!current) return;

    const key = `${this.page}|${this.#hover}|${this.#pressed}|${current.title}|${current.body.join('~')}`;
    if (key === this.#cacheKey) return;
    this.#cacheKey = key;

    const ctx = this.#ctx;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    ctx.fillStyle = 'rgba(17, 28, 28, 0.94)';
    ctx.beginPath();
    ctx.roundRect(0, 0, CANVAS_W, CANVAS_H, 28);
    ctx.fill();
    ctx.strokeStyle = 'rgba(33, 212, 141, 0.4)';
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    // Title + page indicator
    ctx.font = `bold ${TITLE_SIZE}px Rubik, sans-serif`;
    ctx.fillStyle = colors.primary ?? '#21d48d';
    ctx.fillText(current.title, MARGIN, MARGIN + TITLE_SIZE / 2, CANVAS_W - MARGIN * 2 - 120);
    ctx.font = 'bold 26px Rubik, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.fillText(`${this.page + 1} / ${this.#pageCount}`, CANVAS_W - MARGIN, MARGIN + TITLE_SIZE / 2);

    // Body
    ctx.textAlign = 'left';
    const maxWidth = CANVAS_W - MARGIN * 2;
    // Hard floor for body text. Without it, over-long copy runs straight over
    // the nav buttons — and the paragraphs past the bottom edge are invisible,
    // so the overflow is silent. Better to drop a line and log it.
    const bodyBottom = CANVAS_H - NAV_H - 24;
    let y = MARGIN + TITLE_SIZE + 34;
    let clipped = false;
    for (const paragraph of current.body) {
      if (y > bodyBottom) { clipped = true; break; }
      if (!paragraph) {
        y += LINE_H / 2;
        continue;
      }
      const bullet = paragraph.startsWith('• ');
      const text = bullet ? paragraph.slice(2) : paragraph;
      const indent = bullet ? 34 : 0;
      ctx.font = `400 ${BODY_SIZE}px Rubik, sans-serif`;
      const lines = wrapText(ctx, text, maxWidth - indent);
      for (let index = 0; index < lines.length; index++) {
        if (y > bodyBottom) { clipped = true; break; }
        if (bullet && index === 0) {
          ctx.fillStyle = colors.primary ?? '#21d48d';
          ctx.fillText('•', MARGIN, y);
        }
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        ctx.fillText(lines[index], MARGIN + indent, y);
        y += LINE_H;
      }
    }
    if (clipped) {
      console.warn(`InfoPanel: page "${current.title}" does not fit — copy was clipped`);
    }

    // Nav row
    const navLabel: Record<Region['id'], string> = {
      back: '◀  Back',
      next: 'Next  ▶',
      close: 'Close',
    };
    const disabled: Record<Region['id'], boolean> = {
      back: this.page === 0,
      next: this.page >= this.#pageCount - 1,
      close: false,
    };
    ctx.textAlign = 'center';
    for (const region of this.#regions) {
      const x = region.x * CANVAS_W;
      const ry = region.y * CANVAS_H;
      const w = region.w * CANVAS_W;
      const h = region.h * CANVAS_H;
      const off = disabled[region.id];
      if (this.#pressed === region.id && !off) {
        ctx.fillStyle = colors.primary ?? '#21d48d';
        ctx.beginPath();
        ctx.roundRect(x, ry, w, h, 16);
        ctx.fill();
      } else {
        ctx.strokeStyle = off
          ? 'rgba(255,255,255,0.12)'
          : (this.#hover === region.id ? (colors.primary ?? '#21d48d') : 'rgba(255,255,255,0.28)');
        ctx.lineWidth = this.#hover === region.id && !off ? 4 : 2;
        ctx.beginPath();
        ctx.roundRect(x, ry, w, h, 16);
        ctx.stroke();
      }
      ctx.font = 'bold 30px Rubik, sans-serif';
      ctx.fillStyle = this.#pressed === region.id && !off
        ? '#111c1c'
        : (off ? 'rgba(255,255,255,0.25)' : '#ffffff');
      ctx.fillText(navLabel[region.id], x + w / 2, ry + h / 2, w - 20);
    }

    this.#texture.needsUpdate = true;
  }

  dispose() {
    this.object.removeFromParent();
    this.object.geometry.dispose();
    (this.object.material as THREE.Material).dispose();
    this.#texture.dispose();
  }
}
